import { Box, Stack } from '@mui/material';
import React, { useEffect, useState } from 'react';
import TextBox from '../../components/TextBox';
import { Add, AddComment, Business, CreditCard, CreditScore, Map, Phone, Pin } from '@mui/icons-material';
import RadioButton from '../../components/RadioButton';
import TextArea from '../../components/TextArea';
import TextCurrency from '../../components/TextCurrency';
import CustomSwitch from '../../components/CustomSwitch';
import API from '../../services/api';
import CustomSnackBar from '../../components/CustomSnackBar';
import { useNavigate } from 'react-router-dom';
import ButtonSubmit from '../../components/ButtonSubmit';
import { useDispatch, useSelector } from 'react-redux';
import { addTab, resetFormData, updateFormData } from '../../tabs/tabsSlice';
import MainText from '../../components/MainText';
import SupplierAbastoConfig from './SupplierAbastoConfig';

const BANK_REQUISITION_FLOW = 'BANK_REQUISITION';


function CreateSupplier() {
  const [openSnack, setOpenSnack] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [snackMessage, setSnackMessage] = useState('');
  const [snackSeverity, setSnackSeverity] = useState('info');
  const [errors, setErrors] = useState({});
  const formData = useSelector((state) => state.tabs.forms.suppliers);

  const dispatch = useDispatch();
  const isAbasto = formData.payment_flow_type === BANK_REQUISITION_FLOW;

  useEffect(() => {
    dispatch(addTab({ label: 'Crear proveedor', path: '/suppliers/create-new' }));
  }, [dispatch]);

  const handleChange = (name, value) => {
    dispatch(updateFormData({ formName: 'suppliers', field: name, value }));
    if (errors?.[name]) {
      const trimmed = typeof value === 'string' ? value.trim() : value;
      if (trimmed) {
        setErrors((prev) => ({ ...prev, [name]: false }));
      }
    }
  };

  const handleFlowChange = (name, enabled) => {
    handleChange('payment_flow_type', enabled ? BANK_REQUISITION_FLOW : 'NORMAL');
    if (enabled && formData.isCredit !== true) {
      handleChange('isCredit', true);
    }
  };

  const normalizePayload = (data) => {
    const trimValue = (v) => (typeof v === 'string' ? v.trim() : v);
    const interestRate = trimValue(data.abasto_interest_rate);
    return {
      ...data,
      companyName: trimValue(data.companyName),
      legalName: trimValue(data.legalName),
      address: trimValue(data.address),
      taxNumber: trimValue(data.taxNumber),
      phone: trimValue(data.phone),
      abasto_bank_name: trimValue(data.abasto_bank_name),
      abasto_agreement_name: trimValue(data.abasto_agreement_name),
      abasto_agreement_number: trimValue(data.abasto_agreement_number),
      abasto_interest_rate: interestRate === '' || interestRate === undefined ? null : interestRate,
    };
  };

  const handleSubmit = async (e, continueCreating = false) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = normalizePayload(formData || {});
      const requiredFields = ['companyName', 'legalName', 'address', 'taxNumber', 'phone'];
      const nextErrors = {};
      requiredFields.forEach((field) => {
        const value = payload?.[field];
        if (value === null || value === undefined || String(value).trim() === '') {
          nextErrors[field] = true;
        }
      });
      if (Object.keys(nextErrors).length > 0) {
        setErrors(nextErrors);
        setOpenSnack(true);
        setSnackMessage('Completa los campos requeridos');
        setSnackSeverity('error');
        setLoading(false);
        return;
      }
      Object.entries(payload).forEach(([field, value]) => {
        if (typeof value === 'string' && value !== formData?.[field]) {
          dispatch(updateFormData({ formName: 'suppliers', field, value }));
        }
      });
      await API.post('/suppliers/', payload);
      setOpenSnack(true);
      setSnackMessage('¡Creado con éxito!');
      setSnackSeverity('success');
      setTimeout(() => {
        setLoading(false);
        if (continueCreating) {
          dispatch(resetFormData({ formName: 'suppliers' }));
        } else {
          navigate('/suppliers/all-suppliers');
          dispatch(resetFormData({ formName: 'suppliers' }));
        }
      }, 1500);
    } catch (error) {
      setOpenSnack(true);
      console.log(error);
      setSnackMessage('Intente de nuevo');
      setSnackSeverity('error');
      setLoading(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Box component="form" onSubmit={(e) => handleSubmit(e, false)}>
        <MainText
          title={'Nuevo proveedor'}
          subtitle={'Ingreso de información'}
          icon={AddComment}
        />

        <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }}>
          <TextBox
            label={'Nombre de la empresa(Nombre corto)'}
            icon={Business}
            type="text"
            required
            minLength={0}
            maxLength={60}
            errorMessage="Campo requerido"
            externalError={Boolean(errors.companyName)}
            externalHelperText="Campo requerido"
            name={'companyName'}
            newValue={formData.companyName}
            onChange={handleChange}
          />
          <TextBox
            label={'Nombre de razón social'}
            icon={Business}
            type="text"
            required
            minLength={0}
            maxLength={60}
            errorMessage="Campo requerido"
            externalError={Boolean(errors.legalName)}
            externalHelperText="Campo requerido"
            name={'legalName'}
            newValue={formData.legalName}
            onChange={handleChange}
            fullWidth
          />
        </Stack>

        <Box sx={{ mt: 0.5 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
            <CustomSwitch
              name={'all_products'}
              onChange={handleChange}
              label={'Mostrar todos los productos'}
              initialValue={Boolean(formData.all_products)}
            />
            <CustomSwitch
              name="abasto_flow"
              onChange={handleFlowChange}
              label="Flujo"
              uncheckedLabel="Normal"
              checkedLabel="Abasto"
              initialValue={isAbasto}
            />
          </Stack>
        </Box>

        {isAbasto && (
          <SupplierAbastoConfig formData={formData} onChange={handleChange} />
        )}

        <TextBox
          label={'Dirección'}
          icon={Map}
          type="text"
          required
          minLength={0}
          maxLength={120}
          width={'575px'}
          errorMessage="Campo requerido"
          externalError={Boolean(errors.address)}
          externalHelperText="Campo requerido"
          name={'address'}
          newValue={formData.address}
          onChange={handleChange}
        />

        <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }}>
          <TextBox
            label={'Nit'}
            icon={Pin}
            type="number"
            required
            minLength={0}
            maxLength={15}
            errorMessage="Válide que sea nit"
            externalError={Boolean(errors.taxNumber)}
            externalHelperText="Campo requerido"
            name={'taxNumber'}
            newValue={formData.taxNumber}
            onChange={handleChange}
          />
          <TextBox
            label={'Número de teléfono'}
            icon={Phone}
            type="number"
            required
            minLength={0}
            maxLength={8}
            errorMessage="Campo requerido"
            externalError={Boolean(errors.phone)}
            externalHelperText="Campo requerido"
            name={'phone'}
            newValue={formData.phone}
            onChange={handleChange}
          />
        </Stack>

        {!isAbasto && (
        <RadioButton
          name={'isCredit'}
          onChange={handleChange}
          initialValue={formData.isCredit}
          renderCreditFields={() => (
            <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }}>
              <TextBox
                label={'Dias de crédito'}
                icon={CreditCard}
                type="number"
                required
                minLength={0}
                maxLength={2}
                errorMessage="Campo requerido"
                name={'creditPeriod'}
                newValue={formData.creditPeriod}
                onChange={handleChange}
              />
              <TextCurrency
                label="Límite de credito"
                icon={CreditScore}
                required
                maxLength={10}
                name={'creditLimit'}
                newValue={formData.creditLimit}
                onChange={handleChange}
              />
            </Stack>
          )}
        />
        )}

        <TextArea name={'comment'} onChange={handleChange} newValue={formData.comment} />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }}>
          <CustomSwitch name={'whagent'} onChange={handleChange} label={'Agente de retención'} initialValue={formData.whagent} />
        </Stack>
        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
          <ButtonSubmit name="Crear" icon={Add} loading={loading} />
          <ButtonSubmit
            name="Crear y continuar"
            icon={Add}
            loading={loading}
            color="primary"
            type="button"
            onClick={(e) => handleSubmit(e, true)}
          />
        </Stack>

        {openSnack && (
          <CustomSnackBar
            open={openSnack}
            setOpen={setOpenSnack}
            message={snackMessage}
            severity={snackSeverity}
          />
        )}
      </Box>
    </Stack>
  );
}

export default CreateSupplier;
